import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const NoQ5VsHubSpotAI = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "NoQ5 vs HubSpot AI (Breeze) — CRM Agent Comparison 2026",
      "url": "https://noq5.ai/noq5-vs-hubspot-ai"
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://noq5.ai" },
        { "@type": "ListItem", "position": 2, "name": "Comparisons", "item": "https://noq5.ai/comparison" },
        { "@type": "ListItem", "position": 3, "name": "NoQ5 vs HubSpot AI", "item": "https://noq5.ai/noq5-vs-hubspot-ai" }
      ]
    }
  ];

  const comparison = [
    { feature: "Works with Salesforce & Pipedrive", noq5: "✅", hubspot: "❌ HubSpot only" },
    { feature: "Autonomous CRM updates", noq5: "✅ 40+ fields from conversations", hubspot: "⚠️ Limited auto-updates within HubSpot" },
    { feature: "Meeting transcription + analysis", noq5: "✅ Built-in for all plans", hubspot: "⚠️ Basic in Sales Hub, limited analysis" },
    { feature: "Cross-platform activity capture", noq5: "✅ Slack, LinkedIn, Zoom, Gmail, Calendar", hubspot: "⚠️ Mostly within HubSpot ecosystem" },
    { feature: "Contact enrichment", noq5: "✅ 30+ fields, 15+ sources", hubspot: "⚠️ Breeze Intelligence (extra credits)" },
    { feature: "Pipeline forecasting", noq5: "✅ Evidence-based AI", hubspot: "⚠️ Basic forecasting tools" },
    { feature: "Deal risk detection", noq5: "✅ Proactive 48h alerts", hubspot: "❌ No proactive risk alerts" },
    { feature: "AI email drafting", noq5: "✅ Context-aware from meetings", hubspot: "✅ Breeze Copilot" },
    { feature: "Setup complexity", noq5: "15 minutes, zero config", hubspot: "Part of HubSpot — requires Sales Hub Pro+" },
    { feature: "Custom field support", noq5: "✅ Auto-detects any CRM schema", hubspot: "✅ HubSpot properties" },
    { feature: "Pricing transparency", noq5: "Simple per-seat", hubspot: "Complex tier system + credit costs" },
  ];

  return (
    <Layout>
      <SEOHead
        title="NoQ5 vs HubSpot AI (Breeze) — CRM Agent Comparison 2026"
        description="NoQ5 vs HubSpot AI Breeze: compare CRM automation, meeting intelligence, enrichment, forecasting, and pricing. See why revenue teams choose NoQ5 over HubSpot's native AI."
        keywords="NoQ5 vs HubSpot AI, HubSpot Breeze alternative, HubSpot AI alternative, HubSpot AI agent comparison, HubSpot Breeze competitor, HubSpot AI CRM, best HubSpot AI alternative, HubSpot Breeze limitations, HubSpot CRM agent"
        canonicalUrl="https://noq5.ai/noq5-vs-hubspot-ai"
        jsonLd={jsonLd}
      />

      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="text-sm text-muted-foreground mb-6">
              <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/comparison" className="hover:text-primary">Comparisons</Link> / <span className="text-foreground">NoQ5 vs HubSpot AI</span>
            </nav>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              <span className="text-primary">NoQ5</span> vs HubSpot AI (Breeze)
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              HubSpot Breeze adds AI features to HubSpot — but it's locked to the HubSpot ecosystem, charges extra for enrichment credits, and doesn't provide true autonomous CRM agent capabilities. Here's the full picture.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl font-bold text-foreground mb-8 text-center">Feature Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-card rounded-xl overflow-hidden border border-border text-sm">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-3 font-semibold text-foreground">Capability</th>
                  <th className="p-3 font-semibold text-primary text-center">NoQ5</th>
                  <th className="p-3 font-semibold text-foreground text-center">HubSpot Breeze AI</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row) => (
                  <tr key={row.feature} className="border-t border-border">
                    <td className="p-3 font-medium text-foreground">{row.feature}</td>
                    <td className="p-3 text-center text-sm bg-primary/5 text-foreground">{row.noq5}</td>
                    <td className="p-3 text-center text-sm text-muted-foreground">{row.hubspot}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
            <h2 className="text-3xl font-bold text-foreground">Why Revenue Teams Outgrow HubSpot AI</h2>
            
            <h3 className="text-2xl font-bold text-foreground">HubSpot AI Is a Feature Layer, Not an Agent</h3>
            <p>HubSpot Breeze adds AI-powered suggestions, email drafting, and basic automation to HubSpot's interface. But it doesn't fundamentally change how your CRM works. Reps still need to log activities, update fields, and maintain data quality. Breeze helps you work faster inside HubSpot — but it doesn't make HubSpot run itself.</p>
            <p>NoQ5 is architecturally different. It's an autonomous agent that operates your CRM on your behalf. It doesn't suggest updates — it makes them. It doesn't summarize meetings — it extracts every data point and writes them to the correct CRM fields. The difference is between a copilot and an autopilot.</p>

            <h3 className="text-2xl font-bold text-foreground">Enrichment Credits Are a Hidden Cost</h3>
            <p>Breeze Intelligence (HubSpot's enrichment feature) charges per credit. A team enriching 1,000 contacts/month can quickly accumulate significant costs on top of their Sales Hub subscription. NoQ5 includes enrichment from 15+ sources in every plan — no credits, no surprises.</p>

            <h3 className="text-2xl font-bold text-foreground">Cross-Platform Blindness</h3>
            <p>If your team uses Slack for deal discussions, Zoom for calls, LinkedIn for prospecting, and Gmail for follow-ups, HubSpot Breeze doesn't see most of that activity natively. NoQ5 captures from all these channels and maps activities to the right HubSpot records automatically.</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Upgrade From HubSpot AI to a Real CRM Agent</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">NoQ5 works with HubSpot — and makes it run itself. Deploy in 15 minutes alongside your existing HubSpot setup.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/trial"><Button size="lg" className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">Try NoQ5 Free <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            <Link to="/crm-agent"><Button size="lg" variant="outline" className="text-lg px-8 py-6 border-border text-foreground hover:bg-accent">How Our CRM Agent Works</Button></Link>
          </div>
        </div>
      </section>

      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-xl font-semibold text-foreground mb-6">More Comparisons</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/noq5-vs-relevance-ai" className="text-primary hover:underline text-sm">NoQ5 vs Relevance AI</Link>
            <Link to="/noq5-vs-agentforce" className="text-primary hover:underline text-sm">NoQ5 vs Salesforce Agentforce</Link>
            <Link to="/comparison/hubspot" className="text-primary hover:underline text-sm">NoQ5 vs HubSpot (General)</Link>
            <Link to="/crm-agent" className="text-primary hover:underline text-sm">CRM AI Agent</Link>
            <Link to="/crm-enrichment-agent" className="text-primary hover:underline text-sm">CRM Enrichment Agent</Link>
            <Link to="/comparison" className="text-primary hover:underline text-sm">All Comparisons</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NoQ5VsHubSpotAI;
